import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
} from "@mui/material";
import { TextValidator } from "react-material-ui-form-validator";
import { Vocabulary } from "../../Utils/Vocabulary";
import { FgoClient } from "./FgoMainPage";
import { useEffect, useState } from "react";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";

type FgoClientFormProps = {
  clientData: FgoClient;
  handleClientChange: (field: string, value: any) => void;
};

export default function FgoClientForm(props: FgoClientFormProps) {
  const { clientData, handleClientChange } = props;
  const [fgoData, setFgoData] = useState({
    countryList: [],
    countyList: [],
    cityList: [],
    typeList: [],
  });

  useEffect(() => {
    const getFgoData = async () => {
      try {
        const countries = (await getData(`${urlEnum.fgo}/tara`)).data.List;
        const types = (await getData(`${urlEnum.fgo}/tipclient`)).data.List;
        setFgoData((prev) => ({
          ...prev,
          countryList: countries,
          typeList: types,
        }));
      } catch (err) {
        console.error("Error fetching FGO data:", err);
      }
    };
    getFgoData();
  }, []);

  useEffect(() => {
    const getCounties = async () => {
      if (clientData.Tara === "ROMANIA") {
        try {
          const counties = (await getData(`${urlEnum.fgo}/judet`)).data.List;
          setFgoData((prev) => ({
            ...prev,
            countyList: counties,
          }));
        } catch (err) {
          console.error("Error fetching counties:", err);
        }
      } else {
        setFgoData((prev) => ({
          ...prev,
          countyList: [],
        }));
      }
    };
    getCounties();
  }, [clientData.Tara]);

  useEffect(() => {
    const getCities = async () => {
      if (clientData.Judet) {
        try {
          const cities = (
            await getData(`${urlEnum.fgo}/localitati?judet=${clientData.Judet}`)
          ).data.List;
          setFgoData((prev) => ({
            ...prev,
            cityList: cities,
          }));
        } catch (err) {
          console.error("Error fetching cities:", err);
        }
      } else {
        setFgoData((prev) => ({
          ...prev,
          cityList: [],
        }));
      }
    };
    getCities();
  }, [clientData.Judet]);

  return (
    <Grid container spacing={2} style={{ marginTop: 10 }}>
      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="name"
          label={Vocabulary.name}
          fullWidth
          variant="outlined"
          value={clientData.Denumire}
          validators={["required"]}
          errorMessages={[Vocabulary.requiredField]}
          onChange={(e: any) => handleClientChange("Denumire", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="uniqueCode"
          label={Vocabulary.uniqueCode}
          fullWidth
          variant="outlined"
          value={clientData.CodUnic}
          onChange={(e: any) => handleClientChange("CodUnic", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="NrRegCom"
          label={Vocabulary.cui}
          fullWidth
          variant="outlined"
          value={clientData.NrRegCom}
          onChange={(e: any) => handleClientChange("NrRegCom", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="email"
          label={Vocabulary.email}
          fullWidth
          variant="outlined"
          value={clientData.Email}
          onChange={(e: any) => handleClientChange("Email", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="phone"
          label={Vocabulary.phone}
          fullWidth
          variant="outlined"
          value={clientData.Telefon}
          onChange={(e: any) => handleClientChange("Telefon", e.target.value)}
        />
      </Grid>

      {/* Country */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          id="country"
          freeSolo
          options={fgoData.countryList}
          value={
            fgoData.countryList.find((c: any) => c.Nume === clientData.Tara) ||
            null
          }
          getOptionLabel={(option: any) => option.Nume}
          isOptionEqualToValue={(opt: any, val: any) => opt.Nume === val.Nume}
          onChange={(event: any, value: any) => {
            handleClientChange("Tara", value?.Nume || "");
            handleClientChange("Judet", "");
            handleClientChange("Localitate", "");
          }}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.country}
              variant="outlined"
              value={clientData.Tara}
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>

      {/* County */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          id="county"
          freeSolo
          options={fgoData.countyList}
          value={
            fgoData.countyList.find((c: any) => c.Nume === clientData.Judet) ||
            null
          }
          getOptionLabel={(option: any) => option.Nume}
          isOptionEqualToValue={(opt: any, val: any) => opt.Nume === val.Nume}
          onChange={(event: any, value: any) => {
            handleClientChange("Judet", value?.Nume || "");
            handleClientChange("Localitate", "");
          }}
          disabled={!clientData.Tara}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.county}
              variant="outlined"
              value={clientData.Judet}
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>

      {/* City */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          id="city"
          freeSolo
          options={fgoData.cityList}
          value={
            fgoData.cityList.find(
              (c: any) => c.Nume === clientData.Localitate
            ) || null
          }
          getOptionLabel={(option: any) => option.Nume}
          isOptionEqualToValue={(opt: any, val: any) => opt.Nume === val.Nume}
          onChange={(event: any, value: any) => {
            handleClientChange("Localitate", value?.Nume || "");
          }}
          disabled={!clientData.Judet}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.city}
              variant="outlined"
              value={clientData.Localitate}
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
            />
          )}
        />
      </Grid>

      {/* Address */}
      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="Adresa"
          label={Vocabulary.address}
          fullWidth
          variant="outlined"
          value={clientData.Adresa}
          onChange={(e: any) => handleClientChange("Adresa", e.target.value)}
        />
      </Grid>

      {/* Client Type */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          id="type"
          freeSolo
          options={fgoData.typeList}
          value={
            fgoData.typeList.find((t: any) => t.Nume === clientData.Tip) || null
          }
          getOptionLabel={(option: any) => option.Nume}
          isOptionEqualToValue={(opt: any, val: any) => opt.Nume === val.Nume}
          onChange={(event: any, value: any) => {
            handleClientChange("Tip", value?.Nume || "");
          }}
          renderInput={(params: any) => (
            <TextValidator
              {...params}
              fullWidth
              label={Vocabulary.clientType}
              validators={["required"]}
              errorMessages={[Vocabulary.requiredField]}
              variant="outlined"
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="IdExtern"
          label={Vocabulary.externId}
          fullWidth
          variant="outlined"
          value={clientData.IdExtern}
          onChange={(e: any) => handleClientChange("IdExtern", e.target.value)}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextValidator
          size="small"
          name="ContBancar"
          label={Vocabulary.bankAccount}
          fullWidth
          variant="outlined"
          value={clientData.ContBancar}
          onChange={(e: any) =>
            handleClientChange("ContBancar", e.target.value)
          }
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormGroup>
          <FormControlLabel
            onChange={() => {
              handleClientChange("PlatitorTVA", !clientData.PlatitorTVA);
            }}
            checked={clientData.PlatitorTVA ? true : false}
            control={<Checkbox />}
            label={Vocabulary.payVAT}
          />
        </FormGroup>
      </Grid>
    </Grid>
  );
}
